import React, { useEffect, useState, useMemo } from "react";
import { Box, Stack } from '@mui/material';
import { useNavigate } from "react-router";
import TopBar from "../../../../../../component/TopBar";
import DataTable from "../../../../../../component/DataTable";
import MainBox from "../../../../container/MainBox";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import { SearchInput, StatCard } from "../../../../../../component/listing";
import { getActiveLegalUnit } from "../../../../../../services/globalData";
import { getAllExpenseCategory } from "../../../../../../services/expense";
import { getEmployeeNames } from '../../../../../../utils/getEmployeeNames';
import { formatDateByDay } from "../../../../../../utils/formatDate";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens } from '../../../../../../theme';

const ExpenseTypeListing = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const legalUnit = getActiveLegalUnit();
      const res = await getAllExpenseCategory(legalUnit?._id);
      if (res?.success === false) throw new Error(res?.message || "Failed to load expense categories");
      const combined = await getEmployeeNames(res.data, (res.data || []).map((d) => d.createdBy), "createdBy");
      setData(Array.isArray(combined) ? combined.reverse() : []);
    } catch (err) {
      setError(true);
      notify.error(err?.message || "Failed to load expense categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((c) => (c.name || "").toLowerCase().includes(q) || (c.code || "").toLowerCase().includes(q));
  }, [data, search]);

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Expense Category"} />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: "1.5rem" }}>
        <StatCard label="Total categories" value={data.length} />
      </Stack>

      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: "1.25rem" }}>
          <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search category name or code" />
          <BtnPrimary to="form" sx={{ borderRadius: "8px", whiteSpace: "nowrap" }}>
            + Create Expense Category
          </BtnPrimary>
        </Stack>

        <DataTable
          rows={filtered}
          loading={loading}
          error={error}
          onRetry={fetchData}
          errorTitle="Couldn't load categories"
          empty={{
            title: data.length === 0 ? "No expense categories yet" : "No matches",
            description: data.length === 0
              ? "Create categories (e.g. Travel, Meals) used in expense policies."
              : "Try a different search.",
          }}
          onRowClick={(c) => navigate("form", { state: { typeId: c._id } })}
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={[
            { key: "code", label: "Code", render: (c) => c.code || "—" },
            // The name is the thing you came to find, so it carries the weight.
            { key: "name", label: "Category", cellSx: { fontWeight: 600 }, render: (c) => c.name || "—" },
            { key: "policies", label: "Used in Policies", render: (c) => c?.policy?.length || 0 },
            {
              key: "creator",
              label: "Created By",
              render: (c) => (c?.employee ? `${c.employee.fname || ""} ${c.employee.lname || ""}`.trim() : "—"),
            },
            { key: "updated", label: "Last Modified", cellSx: { color: tokens.textMuted }, render: (c) => formatDateByDay(c.updatedAt) },
          ]}
        />
      </MainBox>
    </Box>
  );
};

export default ExpenseTypeListing;
